/* ═══════════════════════════════════════
   SWF Merch — client-side filtering, sorting
   and sticky-rail pin state for the merch shop.
   ═══════════════════════════════════════ */

class SwfMerchFilters extends HTMLElement {
  connectedCallback() {
    this.state = { type: new Set(), size: new Set(), color: new Set() };
    this.grid = document.querySelector('[data-merch-grid]');
    this.cards = this.grid ? Array.from(this.grid.querySelectorAll('[data-merch-card]')) : [];
    this.emptyEl = document.querySelector('[data-merch-empty]');
    this.countEl = this.querySelector('[data-merch-count]');
    this.summaryEl = this.querySelector('.swf-merch-filters__summary');
    this.summaryText = this.querySelector('[data-merch-summary]');
    this.sortSelect = this.querySelector('[data-merch-sort]');
    this.totalCount = parseInt(this.dataset.total, 10) || this.cards.length;

    this.bindTriggers();
    this.bindCheckboxes();
    this.bindClear();
    this.bindSort();
    this.bindOutsideClick();
    this.observePin();

    document.addEventListener('click', (e) => {
      const reset = e.target.closest('[data-merch-reset]');
      if (reset) this.resetAll();
    });
  }

  bindTriggers() {
    this.querySelectorAll('[data-opens]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = btn.dataset.opens;
        const field = btn.closest('.swf-merch-filters__field');
        const isOpen = field.classList.contains('is-open');
        this.closeAllPopovers();
        if (!isOpen) {
          field.classList.add('is-open');
          field.querySelector(`[data-popover-id="${id}"]`).hidden = false;
        }
      });
    });
  }

  bindCheckboxes() {
    this.querySelectorAll('[data-filter]').forEach((cb) => {
      cb.addEventListener('change', () => {
        const group = cb.dataset.filter;
        const value = cb.dataset.value;
        if (cb.checked) this.state[group].add(value);
        else this.state[group].delete(value);
        this.updateTriggerLabel(group);
        this.applyFilters();
      });
    });
  }

  bindClear() {
    this.querySelectorAll('[data-clear]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.clear;
        this.state[group].clear();
        this.querySelectorAll(`[data-filter="${group}"]`).forEach((cb) => (cb.checked = false));
        this.updateTriggerLabel(group);
        this.applyFilters();
      });
    });

    const clearAll = this.querySelector('[data-clear-all]');
    if (clearAll) clearAll.addEventListener('click', () => this.resetAll());
  }

  bindSort() {
    if (!this.sortSelect) return;
    this.sortSelect.addEventListener('change', () => this.applySort());
  }

  bindOutsideClick() {
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeAllPopovers();
    });
  }

  observePin() {
    const hero = this.closest('.swf-merch-hero');
    const sentinel = hero?.querySelector('.swf-merch-hero__sentinel');
    if (!hero || !sentinel) return;
    const io = new IntersectionObserver(
      ([entry]) => hero.classList.toggle('is-pinned', !entry.isIntersecting),
      { rootMargin: '-1px 0px 0px 0px', threshold: [0, 1] }
    );
    io.observe(sentinel);
  }

  closeAllPopovers() {
    this.querySelectorAll('.swf-merch-filters__field.is-open').forEach((f) => {
      f.classList.remove('is-open');
      const pop = f.querySelector('.swf-merch-filters__popover');
      if (pop) pop.hidden = true;
    });
  }

  updateTriggerLabel(group) {
    const field = this.querySelector(`[data-popover="${group}"]`);
    if (!field) return;
    const valueEl = field.querySelector('.swf-merch-filters__trigger-value');
    const set = this.state[group];
    if (set.size === 0) {
      field.classList.remove('has-value');
      valueEl.textContent = valueEl.dataset.default;
    } else {
      field.classList.add('has-value');
      valueEl.textContent = set.size === 1 ? this.prettify([...set][0]) : `${set.size} selected`;
    }
  }

  prettify(handle) {
    return handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  matches(card) {
    const cardSizes = (card.dataset.sizes || '').split(',').filter(Boolean);
    const cardColors = (card.dataset.colors || '').split(',').filter(Boolean);
    const cardType = card.dataset.type || '';

    if (this.state.type.size && !this.state.type.has(cardType)) return false;
    if (this.state.size.size && !cardSizes.some((s) => this.state.size.has(s))) return false;
    if (this.state.color.size && !cardColors.some((c) => this.state.color.has(c))) return false;
    return true;
  }

  applyFilters() {
    let visible = 0;
    this.cards.forEach((card) => {
      const ok = this.matches(card);
      card.hidden = !ok;
      if (ok) visible += 1;
    });

    if (this.countEl) this.countEl.textContent = visible;
    if (this.emptyEl) this.emptyEl.hidden = visible !== 0;

    const activeCount = this.state.type.size + this.state.size.size + this.state.color.size;
    if (this.summaryEl) {
      if (activeCount === 0) {
        this.summaryEl.hidden = true;
      } else {
        this.summaryEl.hidden = false;
        if (this.summaryText) {
          this.summaryText.textContent = `${visible} of ${this.totalCount} pieces · ${activeCount} filter${activeCount === 1 ? '' : 's'} active`;
        }
      }
    }

    this.applySort();
  }

  applySort() {
    if (!this.grid || !this.sortSelect) return;
    const value = this.sortSelect.value;
    const sortable = this.cards.filter((c) => !c.hidden);
    const sorted = [...sortable].sort((a, b) => {
      switch (value) {
        case 'price-asc':
          return parseInt(a.dataset.price, 10) - parseInt(b.dataset.price, 10);
        case 'price-desc':
          return parseInt(b.dataset.price, 10) - parseInt(a.dataset.price, 10);
        case 'title-asc':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        default:
          return 0;
      }
    });
    sorted.forEach((card) => this.grid.appendChild(card));
  }

  resetAll() {
    Object.values(this.state).forEach((set) => set.clear());
    this.querySelectorAll('[data-filter]').forEach((cb) => (cb.checked = false));
    ['type', 'size', 'color'].forEach((g) => this.updateTriggerLabel(g));
    if (this.sortSelect) this.sortSelect.value = 'manual';
    this.applyFilters();
  }
}

if (!customElements.get('swf-merch-filters')) {
  customElements.define('swf-merch-filters', SwfMerchFilters);
}
